import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { Loader2 } from 'lucide-react'
import api from '@/lib/api/axios'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface VaultSummaryEntry {
    vaultName: string
    vaultID?: string
    anomalyCount: number
    highSeverityCount?: number
    objectCount?: number
}

export function VaultLocationTab() {
    const { t } = useTranslation()

    const { data: entries = [], isLoading } = useQuery({
        queryKey: ['analytics', 'vault-summary'],
        queryFn: async () => {
            const res = await api.get('/api/analytics/vault-summary')
            return (res.data?.data || res.data || []) as VaultSummaryEntry[]
        },
    })

    const chartOption = useMemo((): EChartsOption => {
        if (entries.length === 0) return {}

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const p = Array.isArray(params) ? params[0] : params
                    const entry = entries[p.dataIndex]
                    if (!entry) return ''
                    let html = `<div style="font-weight:600">${entry.vaultName}</div>`
                    html += `<div>Anomalies: <b>${entry.anomalyCount}</b></div>`
                    if (entry.highSeverityCount != null) html += `<div>High Severity: <b>${entry.highSeverityCount}</b></div>`
                    if (entry.objectCount != null) html += `<div>Objects: <b>${entry.objectCount}</b></div>`
                    return html
                },
            },
            grid: { left: 50, right: 20, top: 20, bottom: 60 },
            xAxis: {
                type: 'category',
                data: entries.map((e) => e.vaultName),
                axisLabel: { fontSize: 10, color: '#888', rotate: 30 },
                axisTick: { show: false },
                axisLine: { show: false },
            },
            yAxis: {
                type: 'value',
                name: 'Anomaly Count',
                nameTextStyle: { fontSize: 10, color: '#888' },
                axisLabel: { fontSize: 10, color: '#888' },
                splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
            },
            series: [
                {
                    type: 'bar',
                    data: entries.map((e) => e.anomalyCount),
                    itemStyle: {
                        color: '#3b82f6',
                        borderRadius: [4, 4, 0, 0],
                    },
                    barMaxWidth: 50,
                },
            ],
        }
    }, [entries])

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('analytics.vaultLocation', 'Vault & Location Summary')}</CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Loading vault summary...
                    </div>
                ) : entries.length === 0 ? (
                    <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                        No vault summary data available.
                    </div>
                ) : (
                    <>
                        <ReactECharts option={chartOption} style={{ height: 350 }} opts={{ renderer: 'canvas' }} />

                        <div className="mt-6 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                        <th className="pb-2 pr-4 font-medium">Vault</th>
                                        <th className="pb-2 pr-4 font-medium">Anomalies</th>
                                        <th className="pb-2 pr-4 font-medium">High Severity</th>
                                        <th className="pb-2 font-medium">Objects</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((e) => (
                                        <tr key={e.vaultName} className="border-b last:border-0">
                                            <td className="py-2 pr-4 font-medium">{e.vaultName}</td>
                                            <td className="py-2 pr-4">{e.anomalyCount}</td>
                                            <td className="py-2 pr-4">{e.highSeverityCount ?? '—'}</td>
                                            <td className="py-2">{e.objectCount ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    )
}
